import React, { useContext, useState } from "react";
import { produce } from "immer";
import useSWR, { mutate } from "swr";

import {
  Box,
  Flex,
  IconHeartOutline,
  Link,
  NoSSR,
  NotificationBadge,
  Span,
  Text,
} from "@catchoftheday/cg-components";
import { WishlistContext } from "@catchoftheday/common-vendors";

import { fetcher } from "../../../api/index";
import { HeaderTile } from "../../../components/HeaderTile";
import { ListItemPlaceHolder } from "../../../components/ListItemPlaceHolder";
import {
  HEADER_META_DATA_API,
  HEADER_WISHLIST_API,
  HEADER_WISHLIST_REMOVE_ITEM_API,
} from "../../../consts/index";
import {
  AxiosResponse,
  HeaderMetaData,
  HeaderWishList,
  WishListItem,
} from "../../../types/index";
import { HeaderTileContainer } from "../HeaderTileContainer";
import { WishListItems } from "../WishListItems";

export const MAX_DISPLAY_WISHLIST_ITEMS = 5;
const DISABLE_WISHLIST_ROUTES = ["/wishlist"];

export type WishListProps = {
  isLoggedIn?: boolean;
  wishItemsAmount?: number;
};

export const WishList: React.FC<WishListProps> = ({
  isLoggedIn = false,
  wishItemsAmount = 11,
}) => {
  const [shouldFetch, setShouldFetch] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { setDeletedWishlistProductId } = useContext(WishlistContext);
  const shouldRenderWishlistContent =
    typeof window != "undefined" &&
    !DISABLE_WISHLIST_ROUTES.some((path) =>
      window.location?.pathname.includes(path)
    );

  const { data: wishListResponse, isValidating } = useSWR<
    AxiosResponse<HeaderWishList>
  >(
    shouldRenderWishlistContent && shouldFetch && isLoggedIn
      ? HEADER_WISHLIST_API
      : null,
    fetcher
  );

  const wishListData = wishListResponse?.data;
  const wishListItems = wishListData?.items || [];

  async function onItemRemoveHandler(item: WishListItem) {
    setIsLoading(true);
    try {
      const result = await fetcher({
        url: HEADER_WISHLIST_REMOVE_ITEM_API,
        method: "DELETE",
        data: {
          productId: item.productId,
          productSkuId: item.productSkuId,
        },
      });

      mutate(
        HEADER_META_DATA_API,
        async (currentData: AxiosResponse<HeaderMetaData>) =>
          produce(currentData, (state) => {
            state.data.wishlistItemCount -= 1;
          })
      );

      mutate(HEADER_WISHLIST_API, result, false);
      setDeletedWishlistProductId(`${item.productId}`);
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  }

  const renderWishListContent = () => {
    const shouldDisplayViewAll = wishItemsAmount > MAX_DISPLAY_WISHLIST_ITEMS;
    const shouldRenderItems = wishListItems.length > 0;
    const shouldShowPlaceholder =
      isLoggedIn && isValidating && !shouldRenderItems;
    const shouldShowLoadingStatus = isLoggedIn && (isLoading || isValidating);

    return (
      <Flex
        width="400px"
        flexDirection="column"
        color="typography.text"
        boxShadow="0 2px 4px 0 rgba(0,0,0,0.50)"
        bg="white"
        height={shouldShowPlaceholder ? "280px" : undefined}
        data-testid="wishlist-body"
      >
        {shouldShowPlaceholder ? (
          <Box width="100%" p="16px">
            <ListItemPlaceHolder numberOfItems={3} py="13px" />
          </Box>
        ) : (
          <Box
            data-testid="wishlist-container"
            css={{
              opacity: shouldShowLoadingStatus ? 0.5 : 1,
              pointerEvents: shouldShowLoadingStatus ? "none" : "auto",
            }}
          >
            {shouldRenderItems ? (
              <Box px="18px" py="16px">
                <Text mb={0} fontSize="base" fontWeight="semibold">
                  My Wishlist
                </Text>
                <WishListItems
                  items={wishListItems.slice(0, MAX_DISPLAY_WISHLIST_ITEMS)}
                  onItemRemove={onItemRemoveHandler}
                />
                {shouldDisplayViewAll && (
                  <Box
                    py="15px"
                    textAlign="center"
                    borderTop="1px solid"
                    borderColor="ui.borderColor"
                  >
                    <Link
                      href="/my-account/wishlist"
                      data-tracking="header-app-wishlist-view-all"
                    >
                      <Span
                        textTransform="uppercase"
                        fontSize="base"
                        fontWeight="normal"
                      >
                        View All
                      </Span>
                    </Link>
                  </Box>
                )}
              </Box>
            ) : (
              <Box px="16px" py="24px" textAlign="center">
                {isLoggedIn ? (
                  <Span fontWeight="bold">
                    You have no items in your wishlist.
                  </Span>
                ) : (
                  <Span fontWeight="bold">
                    <Link
                      href="/login"
                      data-tracking="header-app-wishlist-login"
                    >
                      Login
                    </Link>{" "}
                    {` or `}
                    <Link
                      href="/register"
                      data-tracking="header-app-wishlist-sign-up"
                    >
                      Sign Up
                    </Link>{" "}
                    to view your wishlist
                  </Span>
                )}
              </Box>
            )}
          </Box>
        )}
      </Flex>
    );
  };

  const wishListIcon = ({ isOpen }) => (
    <HeaderTileContainer
      data-testid="wishlist-reference"
      hasRightBorder
      flexProps={{
        px: [null, null, "0.875rem", null, "1.53rem"],
      }}
      linkProps={{
        "aria-label": "Header Wishlist",
        href: "/my-account/wishlist",
        backgroundColor: isOpen ? "white" : undefined,
        color: isOpen ? "secondary" : "inherit",
        className: "js-dnt",
        "data-tracking": "header-app-wishlist",
      }}
    >
      <Box position="relative">
        {wishItemsAmount > 0 && (
          <NoSSR>
            <NotificationBadge
              text={""}
              width="12px"
              minWidth="auto"
              height="12px"
              top="-2px"
              right="-2px"
              data-testid="wish-items-amount"
            />
          </NoSSR>
        )}
        <IconHeartOutline width={"32px"} height={"30px"} title="Wishlist" />
      </Box>
    </HeaderTileContainer>
  );

  return (
    <HeaderTile
      popoverProps={{
        placement: "bottom-end",
        modifier: [
          {
            name: "offset",
            enabled: true,
            options: {
              offset: [85, 0],
            },
          },
        ],
      }}
      onTileShow={(show) => setShouldFetch(show)}
      icon={wishListIcon}
    >
      {shouldRenderWishlistContent && renderWishListContent()}
    </HeaderTile>
  );
};

export default WishList;
